import React, {FunctionComponent, memo, MouseEvent, useMemo} from 'react';
import './EntryList.less';
import {EntryCard} from '../entry-card/EntryCard.tsx';
import {ListItem} from 'elements/list-item/ListItem.tsx';
import {List} from 'elements/list/List.tsx';
import {MenuEntry, MenuEntryId} from '../../../helpers/menu.helper.types';
import {MenuEntryModifierSelections, ModifierGroup, ModifierGroupId} from '../../../helpers/modifier.helper.types';

interface EntryListProps {
	entries: Array<MenuEntry>,
	allModifierGroups: Array<ModifierGroup>,
	menuModifierSelections: { [key: number]: MenuEntryModifierSelections },
	isNotPossibleToOrder: boolean,
	onModifierGroupClick: (e: MouseEvent, modifierGroupId: ModifierGroupId, menuEntryId: MenuEntryId) => void,
	onAddClick: (e: MouseEvent, menuEntryId: MenuEntryId, quantity: number, modifierIds: MenuEntryModifierSelections, modifierValidationErrors: string) => void,
	onEntryClick: (e: MouseEvent, menuEntryId: MenuEntryId) => void,
	expandedEntryId: MenuEntryId,
	onAllergensClick: (allergens: string, e: MouseEvent, entryId: number) => void,
	entryQuantities: any[];
	onEntryQuantityChange: (entryId, quantity) => void;
	cartNotEmpty?: boolean;
}

export const EntryList = memo<EntryListProps>((
	{
		entries = [],
		allModifierGroups = [],
		menuModifierSelections,
		isNotPossibleToOrder,
		onAddClick,
		onModifierGroupClick,
		onEntryClick,
		expandedEntryId,
		onAllergensClick,
		entryQuantities,
		onEntryQuantityChange,
		cartNotEmpty
	}
) => {

	const modifierGroupsByEntry = useMemo(() => entries.reduce((accum, entry) => {
		return {
			...accum,
			[entry.id]: allModifierGroups
				.filter(modifierGroup => entry.modIds && entry.modIds.includes(modifierGroup.modifierGroupId))
				.sort((modifierGroup1, modifierGroup2) => entry.modIds.indexOf(modifierGroup1.modifierGroupId) - entry.modIds.indexOf(modifierGroup2.modifierGroupId))
		};
	}, {}), [entries, allModifierGroups]);

	return (
		<List>
			{entries.map((entry, index) => {
					const isExpanded = expandedEntryId === entry.id;

					function onListItemClick(e) {
						onEntryClick(e, entry.id);
					}

					return (<ListItem
						key={entry.id}
						isResponsive
						onClick={!isExpanded ? onListItemClick : undefined}
					>
						<EntryCard
							id={entry.id}
							name={entry.name}
							description={entry.desc}
							price={entry.price}
							image={entry.fullImagePath}
							allergens={entry.allergens}
							imageThumbnail={entry.fullThumbnailPath}
							modifierGroups={entry.modIds && modifierGroupsByEntry[entry.id]}
							modifierSelections={menuModifierSelections[entry.id]}
							isSoldOut={entry.isSoldOut}
							isExpanded={isExpanded}
							isNotPossibleToOrder={isNotPossibleToOrder}
							onModifierGroupClick={onModifierGroupClick}
							onAddClick={onAddClick}
							onCloseExpandedClick={isExpanded ? onListItemClick : undefined}
							onAllergensClick={(allergens, e) => onAllergensClick(allergens, e, entry.id)}
							quantity={entryQuantities[entry.id] || 1}
							onQuantityChange={(quantity) => onEntryQuantityChange(entry.id, quantity)}
							cartNotEmpty={cartNotEmpty}
						/>
					</ListItem>);
				}
			)}
		</List>
	);
});
